/**
 * useTestData Hook - Facade
 * Composes useTestDataStatus, useTestDataGeneration, and useTestDataDeletion
 * for backward-compatible API
 *
 * @see TECHNICAL_DEBT_ROADMAP.md Phase 3.1
 */

import type { TestDataProgress, TestDataStatus } from '~/types/test-data';
import { useTestDataStatus } from './useTestDataStatus';
import { useTestDataGeneration } from './useTestDataGeneration';
import { useTestDataDeletion } from './useTestDataDeletion';

interface ToastHelpers {
  success: (message: string) => void;
  error: (message: string) => void;
}

interface UseTestDataOptions {
  onComplete?: () => void;
  onError?: (error: string) => void;
  toast?: ToastHelpers;
  revalidate?: () => void;
}

interface UseTestDataReturn {
  // State
  isReady: boolean;
  isGenerating: boolean;
  isDeleting: boolean;
  progress: TestDataProgress | null;
  status: TestDataStatus | null;
  progressModalOpen: boolean;
  removalModalOpen: boolean;

  // Actions
  startGeneration: () => Promise<void>;
  deleteTestData: () => Promise<boolean>;
  fetchStatus: () => Promise<TestDataStatus | null>;
  closeProgressModal: () => void;
  openRemovalModal: () => void;
  closeRemovalModal: () => void;
  confirmRemoval: () => Promise<void>;
  handlePopulateTestData: () => void;
}

/**
 * Main test data hook - composes status, generation, and deletion hooks
 */
export function useTestData(options: UseTestDataOptions = {}): UseTestDataReturn {
  const { onComplete, onError, toast, revalidate } = options;

  // Status management (App Bridge readiness + status fetching)
  const { isReady, status, fetchStatus } = useTestDataStatus();

  // Generation management (SSE streaming + progress modal)
  const {
    isGenerating,
    progress,
    progressModalOpen,
    startGeneration,
    closeProgressModal,
    handlePopulateTestData,
  } = useTestDataGeneration({
    isReady,
    onComplete,
    onError,
    fetchStatus,
  });

  // Deletion management (confirmation modal + API)
  const {
    isDeleting,
    removalModalOpen,
    deleteTestData,
    openRemovalModal,
    closeRemovalModal,
    confirmRemoval,
  } = useTestDataDeletion({
    isReady,
    onError,
    toast,
    revalidate,
    fetchStatus,
  });

  return {
    isReady,
    isGenerating,
    isDeleting,
    progress,
    status,
    progressModalOpen,
    removalModalOpen,
    startGeneration,
    deleteTestData,
    fetchStatus,
    closeProgressModal,
    openRemovalModal,
    closeRemovalModal,
    confirmRemoval,
    handlePopulateTestData,
  };
}
